<template>
  <div class="relative overflow-x-hidden flex-1 px-6 py-6 flex">
    <div class="flex-1">
      <!-- Title Bar -->
      <div
        class="flex items-center justify-between pb-4 border-b border-gray-300"
      >
        <div class="flex items-center space-x-6">
          <h1 class="text-2xl font-semibold text-gray-800">ผู้ดูแลระบบ</h1>
          <button
            :disabled="formAction === 'edit'"
            :class="{ 'cursor-not-allowed': formAction === 'edit' }"
            @click="addAdmin"
            type="button"
            class="
              inline-flex
              items-center
              px-4
              py-2
              border border-transparent
              rounded-md
              shadow-sm
              text-base
              font-semibold
              tracking-wider
              text-white
              bg-cyan-600
              hover:bg-cyan-700
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="-ml-1 mr-2 h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            เพิ่มผู้ดูแลระบบ
          </button>
        </div>

        <div class="flex items-center space-x-3">
          <SearchWidgetResultsList v-model="search" />

          <AppAutoComplete
            v-model="filterStatus"
            :isLoading="loading"
            :isSearch="false"
            :options="[
              { id: 1, text: 'active', value: true },
              { id: 2, text: 'not active', value: false },
            ]"
            label="เลือกตามสถานะ"
            item-text-key="text"
            item-value-key="id"
            class="w-44 flex-shrink-0"
          />
        </div>
      </div>
      <!-- end title bar -->

      <!-- table -->
      <div class="mt-4">
        <AppDataTable
          :isCheckbox="false"
          :options="options"
          :headers="[
            {
              text: 'อีเมล',
              class: 'text-left',
              value: 'email',
            },
            {
              text: 'คำอธิบาย',
              class: 'text-left',
              value: 'description',
            },
            {
              text: 'สถานะ',
              class: 'text-left w-36',
              value: 'is_active',
            },
            {
              text: 'เริ่มต้นใช้งาน',
              class: 'text-left w-44',
              value: 'start_time',
            },
            {
              text: 'หมดอายุ',
              class: 'text-left w-44',
              value: 'expire_time',
            },
          ]"
          :items="filterAdmins.items"
          :loading="loading"
          @sort="sort"
          :isNext="true"
          :rowClassFunction="rowClassFunction"
          @rowClick="onRowClick"
        >
          <template v-slot:is_active="{ item }">
            <span
              v-if="item.is_active"
              class="
                px-2
                inline-flex
                text-xs
                leading-5
                font-semibold
                rounded-full
                bg-green-100
                text-green-800
              "
            >
              Active
            </span>
            <span
              class="
                px-2
                inline-flex
                text-xs
                leading-5
                font-semibold
                rounded-full
                bg-gray-100
                text-gray-800
              "
              v-else
              >Not Active</span
            >
          </template>
          <template v-slot:start_time="{ item }">
            {{ item.start_time | formatDate }}
          </template>
          <template v-slot:expire_time="{ item }">
            {{ item.expire_time | formatDate }}
          </template>
        </AppDataTable>
      </div>
      <!-- end table -->

      <!-- pagination -->
      <div class="py-4 flex items-center justify-start">
        <AppPagination
          :options="options"
          :startNumberItem="startNumberItem"
          :endNumberItem="endNumberItem"
          :totalItems="filterAdmins.total"
          :isPrevPage="isPrevPage"
          :isNextPage="isNextPage"
          @decreasePage="() => options.page--"
          @increasePage="() => options.page++"
          @itemsPerPageChange="() => (options.page = 1)"
        />
      </div>
      <!-- end pagination -->
    </div>

    <transition
      enter-active-class="transition ease-in-out duration-300"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in-out duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        @click="onCloseForm"
        v-if="isOpen && formAction === 'add'"
        class="fixed inset-0 bg-gray-900 bg-opacity-30"
      />
    </transition>

    <!-- form -->
    <AppSlideOverPanel :isOpen="isOpen">
      <FormAdmin
        :formAction="formAction"
        :currentAdmin="currentAdmin"
        :admin="admin"
        @onClose="onCloseForm"
        @onCancel="onCancelForm"
        @isDataChange="(value) => (isDataFormChange = value)"
        @onDelete="onDelete"
      />
    </AppSlideOverPanel>
    <!-- end form -->

    <!-- confirm delete -->
    <AppConfirmDeleteModal
      :show="isDelete"
      :loading="deleteLoading"
      :items="deleteItems"
      :errors="deleteErrors"
      @clear="deleteErrors = []"
      @cancel="onCancelConfirmDelete"
      @submit="onSubmitConfirmDelete"
    >
      <template v-slot:info> คุณต้องลบข้อมูลผู้ดูแลระบบ? </template>
      <template v-slot="{ items }">
        <ul>
          <li
            v-for="item in items"
            :key="item.taff_admin_uuid"
            class="text-base text-red-500 list-disc list-inside"
          >
            {{ item.email }}
          </li>
        </ul>
      </template>
    </AppConfirmDeleteModal>
    <!-- end confirm delete -->

    <!-- confirm leave page -->
    <AppConfirmLeavePageModal
      :show="isConfirmLeavePageShow"
      :type="isConfirmLeavePageType"
      :next="isConfirmLeavePageNextFunction"
      @cancel="onCancelConfirmLeavePage"
      @submit="onSubmitConfirmLeavePage"
    >
      <template v-if="isConfirmLeavePageType === 'form'" v-slot:title
        >ต้องการปิดใช่ไหม</template
      >
      <template v-if="isConfirmLeavePageType === 'form'" v-slot:message
        >คุณยังไม่ได้บันทึกข้อมูลของคุณ
        คุณแน่ใจหรือไม่ว่าต้องการปิดโดยที่ยังไม่บันทึกข้อมูล ?</template
      >
    </AppConfirmLeavePageModal>
    <!-- end confirm leave page -->
  </div>
</template>

<script>
import moment from 'moment'

import AppAutoComplete from '@/components/AppAutoComplete'
import AppConfirmDeleteModal from '@/components/AppConfirmDeleteModal'
import AppConfirmLeavePageModal from '@/components/AppConfirmLeavePageModal'
import AppDataTable from '@/components/AppDataTable'
import AppPagination from '@/components/AppPagination'
import AppSlideOverPanel from '@/components/AppSlideOverPanel'
import FormAdmin from '@/components/FormAdmin'
import SearchWidgetResultsList from '@/components/SearchWidgetResultsList'

export default {
  name: 'AdminList',

  components: {
    SearchWidgetResultsList,
    AppAutoComplete,
    AppDataTable,
    AppPagination,
    AppSlideOverPanel,
    FormAdmin,
    AppConfirmDeleteModal,
    AppConfirmLeavePageModal,
  },

  data() {
    return {
      loading: false,
      search: '',
      options: {
        sortBy: 'email',
        sortDesc: false,
        itemsPerPage: 10,
        page: 1,
      },
      filterStatus: null,
      admin: {},
      currentAdmin: {},
      isOpen: false,
      formAction: '',
      isDelete: false,
      deleteLoading: false,
      deleteItems: [],
      timeout: null,
      isDataFormChange: false,
      isConfirmLeavePageShow: false,
      isConfirmLeavePageType: '',
      isConfirmLeavePageNextFunction: null,
      deleteErrors: [],
    }
  },

  filters: {
    formatDate: function (value) {
      return value ? moment.utc(String(value)).format('DD/MM/YYYY HH:mm') : '-'
    },
  },

  watch: {
    search() {
      this.options.page = 1
    },

    filterStatus() {
      this.options.page = 1
    },
  },

  computed: {
    admins() {
      return this.$store.state.admin.admins
    },

    filterAdmins() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options

      let items = this.admins

      if (this.search) {
        items = items.filter((item) => {
          return Object.values({
            email: item.email,
            is_active: item.is_active ? 'active' : 'not active',
            start_time: item.start_time
              ? moment(item.start_time).format('DD/MM/YYYY')
              : '',
            expire_time: item.expire_time
              ? moment(item.expire_time).format('DD/MM/YYYY')
              : '',
          }).some((s) =>
            s.toString().toLowerCase().includes(this.search.toLowerCase())
          )
        })
      }

      if (this.filterStatus) {
        items = items.filter(
          (item) => item.is_active === this.filterStatus.value
        )
      }

      items = items.sort((a, b) => {
        const sortA = a[sortBy]
        const sortB = b[sortBy]

        if (sortDesc) {
          if (sortA < sortB) return 1
          if (sortA > sortB) return -1
          return 0
        } else {
          if (sortA < sortB) return -1
          if (sortA > sortB) return 1
          return 0
        }
      })

      const total = items.length

      if (itemsPerPage > 0) {
        items = items.slice((page - 1) * itemsPerPage, page * itemsPerPage)
      }

      return { items, total }
    },

    startNumberItem() {
      if (this.filterAdmins.total === 0) return 0

      return (this.options.page - 1) * this.options.itemsPerPage + 1
    },

    endNumberItem() {
      const sum = this.options.page * this.options.itemsPerPage
      if (sum < this.filterAdmins.total) return sum

      return this.filterAdmins.total
    },

    isPrevPage() {
      if (this.options.page === 1) return true

      return false
    },

    isNextPage() {
      if (this.filterAdmins.items.length === 0) return true
      if (this.filterAdmins.length === 0) return true

      return (
        this.options.page ===
        Math.ceil(this.filterAdmins.total / this.options.itemsPerPage)
      )
    },
  },

  created() {
    this.$store.commit('setMenusCustomerActive', false)
    this.$store.commit('setBreadcrumbs', [
      { text: 'ผู้ดูแลระบบ', routeName: 'AdminList' },
    ])

    this.fetchDataFromApi()
  },

  methods: {
    onCancelConfirmLeavePage() {
      this.isConfirmLeavePageShow = false
    },

    onSubmitConfirmLeavePage(obj) {
      this.isDataFormChange = false
      if (obj.type === 'form') {
        this.isConfirmLeavePageShow = false
        this.isOpen = false
        this.currentAdmin = {}
        this.admin = {}
        this.formAction = ''
        return
      }
      if (obj.type === 'page') {
        this.isConfirmLeavePageShow = false
        this.isOpen = false
        this.currentAdmin = {}
        this.admin = {}
        this.formAction = ''
        this.timeout = setTimeout(() => {
          obj.next()
        }, 500)
        return
      }
    },

    onCancelForm() {
      this.isDataFormChange = false
      this.isOpen = false
      this.currentAdmin = {}
      this.admin = {}
      this.formAction = ''
    },

    onCloseForm() {
      if (this.isDataFormChange) {
        this.isConfirmLeavePageShow = true
        this.isConfirmLeavePageType = 'form'
        return
      }
      this.isOpen = false
      this.currentAdmin = {}
      this.admin = {}
      this.formAction = ''
    },

    rowClassFunction(item) {
      if (this.admin.taff_admin_uuid === item.taff_admin_uuid) {
        return 'cursor-pointer bg-gray-200'
      }
      return 'cursor-pointer hover:bg-gray-100'
    },

    onRowClick(item) {
      if (this.admin.taff_admin_uuid === item.taff_admin_uuid) {
        this.onCloseForm()
      } else {
        this.isOpen = true
        this.formAction = 'edit'
        this.currentAdmin = {
          ...item,
          description: item.description || '',
          start_time: item.start_time
            ? moment.utc(item.start_time).format('DD/MM/YYYY HH:mm')
            : '',
          expire_time: item.expire_time
            ? moment.utc(item.expire_time).format('DD/MM/YYYY HH:mm')
            : '',
        }
        this.admin = {
          ...item,
          description: item.description || '',
          start_time: item.start_time
            ? moment.utc(item.start_time).format('DD/MM/YYYY HH:mm')
            : '',
          expire_time: item.expire_time
            ? moment.utc(item.expire_time).format('DD/MM/YYYY HH:mm')
            : '',
        }
      }
    },

    async fetchDataFromApi() {
      try {
        this.loading = true

        await this.$store.dispatch('admin/fetchAdmins')
      } catch (error) {
        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    sort(value) {
      if (this.options.sortBy === value) {
        this.options.sortDesc = !this.options.sortDesc
        this.options.page = 1
      } else {
        this.options.sortBy = value
        this.options.sortDesc = false
        this.options.page = 1
      }
    },

    addAdmin() {
      this.isOpen = true
      this.formAction = 'add'
      this.currentAdmin = {
        description: '',
        is_active: true,
        start_time: '',
        expire_time: '',
      }
      this.admin = {
        description: '',
        is_active: true,
        start_time: '',
        expire_time: '',
      }
    },

    onDelete(item) {
      this.isDelete = true
      this.deleteItems = [{ ...item }]

      this.isOpen = false
    },

    onCancelConfirmDelete() {
      this.isDelete = false

      this.isOpen = true

      this.deleteErrors = []
    },

    async onSubmitConfirmDelete(items) {
      this.deleteLoading = true

      for (const item of items) {
        try {
          await this.$store.dispatch('admin/deleteAdmin', item.taff_admin_uuid)
        } catch (error) {
          if (error.response && error.response.data) {
            this.deleteErrors.push(
              error.response.data.message || 'Something went wrong.'
            )
            this.deleteLoading = false
            return
          }

          console.log(error.response)
        }
      }

      this.deleteLoading = false
      this.isDelete = false
      this.deleteErrors = []

      const notification = {
        type: 'success',
        message: 'Your admin has been deleted!',
      }
      this.$store.dispatch('notification/add', notification)

      this.formAction = ''
    },
  },

  beforeDestroy() {
    clearTimeout(this.timeout)
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDataFormChange) {
      this.isConfirmLeavePageShow = true
      this.isConfirmLeavePageType = 'page'
      this.isConfirmLeavePageNextFunction = next
      return
    }
    if (this.isOpen) {
      this.isOpen = false
      this.timeout = setTimeout(() => {
        next()
      }, 500)
      return
    }

    next()
  },
}
</script>
